import { Button, Alert, AlertTitle, Stack, Typography, fontStacks } from '@munifocus/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  FILE_STAGES,
  type FileDetail,
  type FileStage as FileStageId,
} from '@munifocus/contracts-gov';
import { AppShell } from './AppShell';
import { FileEmergencyBanner } from './FileEmergencyBanner';
import { StageRecord, type StageRecordRow } from './StageRecord';
import { localizeStatuteRef, stageLabelKey, stageLegislativeRef } from '../lib/file-display';

export function StageLayout({
  file,
  stageId,
  children,
  onComplete,
  saving,
  disabled,
  completeLabel,
  record,
}: {
  file: FileDetail;
  stageId: FileStageId;
  children: ReactNode;
  onComplete: () => void;
  saving: boolean;
  disabled?: boolean;
  /** Override the primary action label (defaults to "Complete stage"). */
  completeLabel?: string;
  /**
   * What this stage recorded, for the read-only view.
   *
   * Rendered instead of `children` whenever the file is not at this stage. A
   * stage the file has passed is a record of decisions already relied on; a
   * stage it has not reached has nothing to record yet. Neither is a form.
   */
  record?: StageRecordRow[];
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCurrent = file.stage === stageId;
  const legislativeRef = stageLegislativeRef(file.bylawCategory, stageId);
  // Past or future, and they are different situations: one has answers to show
  // and cannot be revised, the other has nothing yet and cannot be started.
  const isPast = FILE_STAGES.indexOf(stageId) < FILE_STAGES.indexOf(file.stage);
  const currentStageLabel = t(stageLabelKey(file.stage));
  const thisStageLabel = t(stageLabelKey(stageId));

  return (
    <AppShell
      // Back to the file, named by its reference — the toolbar arrow and the
      // footer button are the same move, offered where each hand already is.
      back={{ label: file.reference, onClick: () => navigate(`/files/${file.id}`) }}
      // The bar names the act, not just the noun: "Viewing stage: Intake" on a
      // stage the file has left, "Working stage: Review" on the one in hand.
      title={t(isCurrent ? 'gov.stage.workingTitle' : 'gov.stage.viewingTitle', {
        stage: thisStageLabel,
      })}
      bottomBar={
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={() => navigate(`/files/${file.id}`)}>
            {t('common.actions.back')}
          </Button>
          {/* No Complete button on a stage the file is not at: a disabled
              primary action reads as "something here is missing" when the truth
              is that this stage is not the one being worked. The way forward is
              the current stage, so that is what the button offers. */}
          {isCurrent ? (
            <Button
              fullWidth
              variant="contained"
              disabled={saving || disabled}
              onClick={onComplete}
            >
              {saving ? t('gov.stage.advancing') : (completeLabel ?? t('gov.stage.complete'))}
            </Button>
          ) : (
            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate(`/files/${file.id}/stage/${file.stage}`)}
            >
              {t('gov.stage.goToCurrent', { stage: currentStageLabel })}
            </Button>
          )}
        </Stack>
      }
    >
      <FileEmergencyBanner emergency={file.emergency} />
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: fontStacks.mono }}>
        {file.reference}
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t(stageLabelKey(stageId))}</Typography>
        {legislativeRef && (
          <Typography variant="caption" color="text.secondary">
            {localizeStatuteRef(legislativeRef)}
          </Typography>
        )}
      </Stack>

      {/* Say why before showing what: an officer who reads the fields first and
          the reason second has already tried to type into them. */}
      {!isCurrent && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <AlertTitle>
            {isPast
              ? t('gov.stage.completedTitle', { stage: thisStageLabel })
              : t('gov.stage.notStartedTitle', { stage: thisStageLabel })}
          </AlertTitle>
          {isPast
            ? t('gov.stage.completedBody', { stage_current: currentStageLabel })
            : t('gov.stage.notStartedBody', { stage_current: currentStageLabel })}
        </Alert>
      )}

      <Stack spacing={2} sx={{ mt: 2 }}>
        {isCurrent ? children : <StageRecord rows={record ?? []} />}
      </Stack>
    </AppShell>
  );
}
